import { withTranslation } from 'react-i18next';
import { Card, CardBody, Row, Table } from 'reactstrap';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { Link } from 'react-router-dom';

import {
  formatCellphone,
  formatCurrency,
  formatDate,
  formatDocument,
} from '../../../../helpers/format';
import { translations } from '../../statuses';
import StatusDropdown from './StatusDropdown';
import { Can } from '../../../../helpers/casl';

const statusWithExpirationDate = ['expired', 'released', 'error', 'canceled'];

const render = (card, isLoading) => (
  <>
    <div className="d-flex align-items-center justify-content-between">
      <span className="fs-4 fw-semibold">{card.heading}</span>
      {card?.description ?? null}
    </div>

    {isLoading ? (
      <div className="skeleton card" style={{ height: '100px' }} />
    ) : (
      <Card className="mt-1 p-0">
        <CardBody className="p-0">
          <Table className="table table-striped m-0 text-dark">
            <tbody>
              {card.table.map(({ label, value, extra }, key) => (
                <tr key={`sidebar-consumer-${++key}`}>
                  <td className="fw-semibold" style={{ width: '40%' }}>
                    {label}
                  </td>
                  <td className={extra ? 'd-flex justify-content-between' : ''}>
                    <span className="text-break">{value}</span>
                    <span>{extra ?? null}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </CardBody>
      </Card>
    )}
  </>
);

const GeneralInfo = (props) => {
  const { order, isLoading } = useSelector(
    createSelector(
      (store) => store.Order,
      (slice) => ({
        isLoading: slice.isLoading,
        order: slice.order,
      })
    )
  );

  const accountSettings = useSelector(
    (state) => state.GeneralSettings.accountSettings
  );

  if (!order) {
    return <div></div>;
  }

  const consumer = {
    heading: props.t('order-details.sidebar.consumer.heading'),
    description: (
      <Link
        to={`/carteiras/detalhes/${order?.customerData?.document}`}
        className="d-flex align-items-center gap-2 text-muted"
      >
        {props.t('order-details.sidebar.consumer.extra')}
        <i className="bx bx-show fs-5" />
      </Link>
    ),
    table: [
      {
        label: props.t('order-details.sidebar.consumer.table.name'),
        value: order?.customerData?.name ?? '-',
      },
      {
        label: props.t('order-details.sidebar.consumer.table.document'),
        value: order?.customerData?.document
          ? formatDocument(order?.customerData?.document, accountSettings)
          : '-',
      },
      {
        label: props.t('order-details.sidebar.consumer.table.email'),
        value: order?.customerData?.email ?? '-',
      },
      {
        label: props.t('order-details.sidebar.consumer.table.phone'),
        value: order?.customerData?.cellphone
          ? formatCellphone(order?.customerData?.cellphone)
          : '-',
      },
    ],
  };

  const cashback = {
    heading: props.t('order-details.sidebar.cashback.heading'),
    table: [
      {
        label: props.t('order-details.sidebar.cashback.table.status'),
        value: (
          <span
            className={`badge rounded-pill ${translations[order?.cashback?.status]?.color}`}
          >
            {translations[order?.cashback?.status]?.label}
          </span>
        ),
        extra: (
          <Can I="update" a="sales_management">
            <StatusDropdown icon="bx-edit-alt" order={order} />
          </Can>
        ),
      },
      {
        label: props.t('order-details.sidebar.cashback.table.expiring'),
        value:
          order?.cashback?.available >= 0 &&
          order?.cashback?.expiresAt &&
          statusWithExpirationDate.includes(order?.cashback?.status) &&
          new Date(order.cashback.expiresAt) > new Date()
            ? formatCurrency(order?.cashback?.available)
            : '- ',
      },
      {
        label: props.t('order-details.sidebar.cashback.table.expiring_date'),
        value:
          order?.cashback?.expiresAt &&
          statusWithExpirationDate.includes(order?.cashback?.status)
            ? formatDate(order?.cashback?.expiresAt, 'DD/MM/Y')
            : '-',
      },
      {
        label: props.t('order-details.sidebar.cashback.table.modifier'),
        value: (
          <div className="d-flex flex-column flex-wrap gap-1">
            {order?.cashbackModifiers
              ? order?.cashbackModifiers?.map((modifier) => (
                  <span key={modifier}>{modifier}</span>
                ))
              : '-'}
          </div>
        ),
      },
      {
        label: props.t('minimum_sales_value_to_generate_cashback'),
        value: (
          <div
            className="d-flex align-items-center"
            style={{ minHeight: '48px' }}
          >
            {order?.minimumSalesValueToGenerateCashback >= 0
              ? formatCurrency(order?.minimumSalesValueToGenerateCashback)
              : '-'}
          </div>
        ),
      },
      {
        label: props.t('minimum_accumulation'),
        value: (
          <div
            className="d-flex align-items-center"
            style={{ minHeight: '48px' }}
          >
            {order?.minimumAccumulation >= 0
              ? formatCurrency(order?.minimumAccumulation)
              : '-'}
          </div>
        ),
      },
      {
        label: props.t('maximum_accumulation'),
        value: (
          <div
            className="d-flex align-items-center"
            style={{ minHeight: '48px' }}
          >
            {order?.maximumAccumulation >= 0
              ? formatCurrency(order?.maximumAccumulation)
              : '-'}
          </div>
        ),
      },
      {
        label: props.t('minimum-sales-value-to-redeem'),
        value: (
          <div
            className="d-flex align-items-center"
            style={{ minHeight: '48px' }}
          >
            {order?.minimumSalesValueToRedeem >= 0
              ? formatCurrency(order?.minimumSalesValueToRedeem)
              : '-'}
          </div>
        ),
      },
    ],
  };

  const store = {
    heading: props.t('order-details.sidebar.store.heading'),
    table: [
      {
        label: props.t('order-details.sidebar.store.table.origin'),
        value: order?.origin ? props.t(`order-origin-${order?.origin}`) : '-',
      },
      {
        label: props.t('order-details.sidebar.store.table.channel'),
        value: order?.channel
          ? props.t(`order-channel-${order?.channel}`)
          : '-',
      },
      {
        label: props.t('order-details.sidebar.store.table.store'),
        value: order?.store ?? '-',
      },
      {
        label: props.t('order-details.sidebar.store.table.seller'),
        value: order?.seller ?? '-',
      },
    ],
  };

  return (
    <Row className="g-3">
      {render(consumer, isLoading)}
      {render(cashback, isLoading)}
      {render(store, isLoading)}
    </Row>
  );
};

export default withTranslation()(GeneralInfo);
